import { APP_LOCALE } from "../config/locale";
import type { Invoice, InvoiceAllocation } from "./faturasApi";

/**
 * Valores e descritivo a mostrar numa LINHA da tabela quando há filtro de obra. Numa fatura
 * repartida (Auto partilhado) a linha passa a mostrar a fatia dessa obra — e não o documento
 * inteiro — para as linhas visíveis somarem o total do rodapé, que já conta só a fatia.
 *
 * A aritmética espelha o servidor (InvoiceTotalsCalculator): a fatia só guarda o líquido (s/IVA),
 * pelo que o c/IVA da fatia sai da proporção do cabeçalho, com os mesmos fallbacks de
 * líquido/bruto. Sem líquido por onde comparar, assume-se fatia sem IVA em vez de inventar taxa.
 */

const headerNet = (invoice: Invoice): number =>
  invoice.baseAmount ??
  (invoice.totalAmount != null && invoice.vatAmount != null
    ? invoice.totalAmount - invoice.vatAmount
    : (invoice.totalAmount ?? 0));

const headerGross = (invoice: Invoice): number =>
  invoice.totalAmount ??
  (invoice.baseAmount != null && invoice.vatAmount != null
    ? invoice.baseAmount + invoice.vatAmount
    : (invoice.baseAmount ?? 0));

/** Fatia da obra `workId` numa fatura repartida; undefined = sem filtro, não repartida ou sem fatia. */
export function sliceForWork(
  invoice: Invoice,
  workId: number | null,
): InvoiceAllocation | undefined {
  if (workId == null || !invoice.isSplit) return undefined;
  return invoice.allocations.find((allocation) => allocation.workId === workId);
}

export interface RowAmounts {
  /** s/IVA a mostrar na linha. */
  base: number | null;
  /** c/IVA a mostrar na linha. */
  total: number | null;
  /** Verdadeiro quando os valores são a fatia de uma obra e não o documento inteiro. */
  isSlice: boolean;
}

export function rowAmounts(invoice: Invoice, workId: number | null): RowAmounts {
  const slice = sliceForWork(invoice, workId);
  if (!slice) {
    // O mesmo fallback do rodapé e dos filtros de valor: uma fatura só com Total+IVA mostrava
    // "—" no s/IVA enquanto o total e o filtro a tratavam como Total−IVA — e "s/IVA ≥ 90"
    // devolvia uma linha aparentemente vazia. Sem valor nenhum continua a mostrar "—".
    const hasAnyAmount =
      invoice.baseAmount != null ||
      invoice.totalAmount != null ||
      invoice.vatAmount != null;
    return {
      base: hasAnyAmount ? headerNet(invoice) : null,
      total: hasAnyAmount ? headerGross(invoice) : null,
      isSlice: false,
    };
  }

  const net = headerNet(invoice);
  const gross = headerGross(invoice);
  return {
    base: slice.amount,
    total: net !== 0 ? (slice.amount * gross) / net : slice.amount,
    isSlice: true,
  };
}

/** Descritivo da linha: com filtro de obra, o texto da fatia dessa obra (ex.: o que lá foi
 * aplicado) em vez do descritivo genérico do documento. */
export function rowDescription(
  invoice: Invoice,
  workId: number | null,
): string | null {
  const slice = sliceForWork(invoice, workId);
  return slice?.description ?? invoice.description ?? null;
}

/**
 * Data de entrada no sistema. "Hoje" e "Ontem" por extenso porque é essa a pergunta que se faz a
 * esta coluna — "o que acabou de chegar?" — e uma data igual às outras não a responde de relance.
 */
export const formatArrival = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";

  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);

  const mesmoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (mesmoDia(parsed, hoje)) return "Hoje";
  if (mesmoDia(parsed, ontem)) return "Ontem";
  return parsed.toLocaleDateString(APP_LOCALE);
};
